import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import { DriverSyncStatus } from 'components/Erp/IntegrationTrip';
import DriverStatus from 'components/Erp/DriverStatus/DriverStatus.tsx';
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { Search, Plus, Truck , ChevronLeft, ChevronRight } from "lucide-react";
import { hasAccess } from "utils/access";
import { Vehicle, StatusBadge } from "components/Erp/Display";
import { SectionHeading, EmptyState } from "components/Erp/Presentation";
import DriverInfoCard from "components/DriverInfoCard/DriverInfoCard";
import DriverActions from "./DriverActions";
import s from "../Management.module.scss";
import useDriverList from '../DriverList/useDriverList.ts';
import DriverFilters from '../DriverList/DriverFilters.tsx';

export default function AllDriversPanel() {
  useInterfaceLanguage();
  const user = useSelector((state) => state.user);
  const [expanded, setExpanded] = useState(null);
  const { drivers, pagination: driversPagination, search: driversFilter, setSearch: setDriversFilter,
    statusFilter, changeFilter, transportStatus, changeTransportStatus, setPage, loading, error, reload, submitSearch } = useDriverList();
  return (
    <section className={s.panel}>
      <SectionHeading icon={Truck} title={tr("Транспорт")} description={tr("Транспорт, документы, участие в базах и доступ к кабинету.")}
        actions={hasAccess(user, "drivers_edit") && (
          <NavLink className={s.primary} to="/driver-register-admin/">
            <Plus size={14} />{tr("Зарегистрировать водителя")}</NavLink>
        )} />
      <DriverFilters value={statusFilter} onChange={value => { setExpanded(null); changeFilter(value); }}
        transportStatus={transportStatus} onTransportStatusChange={value => { setExpanded(null); changeTransportStatus(value); }} />
      <form
        className={s.toolbar}
        onSubmit={(e) => {
          e.preventDefault();
          submitSearch();
        }}
      >
        <input
          aria-label={tr("Поиск транспорта")}
          placeholder={tr("Водитель, номер автомобиля, база")}
          value={driversFilter}
          onChange={(e) => setDriversFilter(e.target.value)}
        />
        <button className={s.button}>
          <Search size={14} />{tr("Найти")}</button>
      </form>
      {error && <p className={s.error} role="alert">{tr(error)}</p>}
      {loading && <p className={s.muted} role="status">{tr("Загрузка водителей…")}</p>}
      {!drivers.length && !loading && !error ? (
        <EmptyState icon={Truck} title={tr("Транспорт не найден")} description={tr("Измените поиск или зарегистрируйте нового водителя.")} compact />
      ) : (
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{tr("Автомобиль")}</th>
                <th>{tr("Водитель")}</th>
                <th>{tr("База / контакты")}</th>
                <th>{tr("Поездки")}</th>
                <th>{tr("Статус")}</th>
                <th>{tr("Синхронизация")}</th><th>{tr("Действия")}</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map((driver) => (
                <React.Fragment key={driver.id}>
                  <tr>
                    <td data-label={tr("Автомобиль")}>
                      <Vehicle
                        number={driver.vehicle_number}
                        model={driver.vehicle_model}
                      />
                    </td>
                    <td data-label={tr("Водитель")}>
                      <strong>{tr(driver.employee_name || "Без водителя")}</strong>
                      <div className={s.muted}>#{tr(driver.id)}</div>
                      <div className={s.muted}>{tr('ИНН')}: {driver.inn || '—'}</div>
                    </td>
                    <td data-label={tr("База / контакты")}>
                      {tr(driver.top_base_name || tr("Без базы"))}
                      <div className={s.muted}>
                        {tr(driver.employee_phone || driver.phone || "—")}
                      </div>
                    </td>
                    <td data-label={tr("Поездки")}>{tr(driver.loads_count || 0)}</td>
                    <td data-label={tr("Статус")}>
                      <DriverStatus driver={driver} />
                      <StatusBadge tone={driver.is_active ? 'success' : 'neutral'}>
                        {tr(driver.is_active ? 'Активен' : 'Не активен')}
                      </StatusBadge>
                    </td>
                    <td data-label={tr("Синхронизация")}><DriverSyncStatus driver={driver} /></td><td data-label={tr("Действия")}>
                      <DriverActions driver={driver} onChanged={reload} documentsExpanded={expanded === driver.id}
                        onDocumentsToggle={() => setExpanded(prev => prev === driver.id ? null : driver.id)} />
                    </td>
                  </tr>
                  {tr(expanded === driver.id && (
                    <tr>
                      <td colSpan={7}>
                        <DriverInfoCard driver={driver} />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className={s.pagination}>
        <span>{tr("Всего: ")}{tr(driversPagination.count)}{tr(" · Страница")}{" "}
          {tr(driversPagination.page || 1)}
        </span>
        <button
          className={s.button}
          disabled={loading || !driversPagination.previous}
          onClick={() => setPage(driversPagination.page - 1)}
        ><ChevronLeft size={14} aria-hidden="true"/>{tr("Назад")}</button>
        <button
          className={s.button}
          disabled={loading || !driversPagination.next}
          onClick={() => setPage(driversPagination.page + 1)}
        ><ChevronRight size={14} aria-hidden="true"/>{tr("Далее")}</button>
      </div>
    </section>
  );
}
